import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Slider,
  Typography,
  CircularProgress,
} from '@mui/material';
import Cropper, { Area } from 'react-easy-crop';

interface ImageCropDialogProps {
  open: boolean;
  imageSrc: string;
  aspectRatio?: number;
  onClose: () => void;
  onCropComplete: (croppedImageFile: File) => void;
}

const ImageCropDialog: React.FC<ImageCropDialogProps> = ({
  open,
  imageSrc,
  aspectRatio = 2, // 1200x600 = 2:1
  onClose,
  onCropComplete,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const croppedAreaPixelsRef = useRef<Area | null>(null);

  const onCropChange = useCallback((crop: { x: number; y: number }) => {
    setCrop(crop);
  }, []);

  const onZoomChange = useCallback((zoom: number) => {
    setZoom(zoom);
  }, []);

  const onCropCompleteCallback = useCallback(
    (croppedArea: Area, croppedAreaPixels: Area) => {
      setCroppedAreaPixels(croppedAreaPixels);
      croppedAreaPixelsRef.current = croppedAreaPixels;
    },
    []
  );

  // Resetar quando o dialog abrir
  useEffect(() => {
    if (open) {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      croppedAreaPixelsRef.current = null;
      setIsProcessing(false);
    }
  }, [open]);

  const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const image = new Image();
      
      // Detectar se é uma blob URL ou data URL (não precisa de crossOrigin)
      const isBlobOrDataUrl = url.startsWith('blob:') || url.startsWith('data:');
      
      // Para blob URLs e data URLs, não definir crossOrigin
      // Para URLs externas, tentar com crossOrigin para evitar tainted canvas
      if (!isBlobOrDataUrl) {
        image.crossOrigin = 'anonymous';
      }
      
      const timeout = setTimeout(() => {
        reject(new Error('Timeout ao carregar imagem'));
      }, 30000); // 30 segundos timeout
      
      image.addEventListener('load', () => {
        clearTimeout(timeout);
        console.log('Imagem carregada:', { 
          width: image.width, 
          height: image.height,
          urlType: isBlobOrDataUrl ? 'blob/data' : 'external'
        });
        resolve(image);
      });
      
      image.addEventListener('error', (error) => {
        clearTimeout(timeout);
        console.error('Erro ao carregar imagem:', error, url.substring(0, 100));
        
        // Se falhar com crossOrigin em URL externa, tentar sem crossOrigin
        if (!isBlobOrDataUrl && image.crossOrigin) {
          console.log('Tentando recarregar sem crossOrigin...');
          const retryImage = new Image();
          const retryTimeout = setTimeout(() => {
            reject(new Error('Timeout ao recarregar imagem'));
          }, 30000);
          
          retryImage.addEventListener('load', () => {
            clearTimeout(retryTimeout);
            resolve(retryImage);
          });
          retryImage.addEventListener('error', () => {
            clearTimeout(retryTimeout);
            reject(new Error(`Erro ao carregar imagem: ${url.substring(0, 100)}`));
          });
          retryImage.src = url;
        } else {
          reject(new Error(`Erro ao carregar imagem: ${url.substring(0, 100)}`));
        }
      });
      
      image.src = url;
    });

  const getCroppedImg = async (
    imageSrc: string,
    pixelCrop: Area
  ): Promise<Blob> => {
    try {
      const image = await createImage(imageSrc);
      
      // Validar coordenadas antes de processar
      if (
        pixelCrop.x < 0 || 
        pixelCrop.y < 0 || 
        pixelCrop.width <= 0 || 
        pixelCrop.height <= 0
      ) {
        throw new Error(`Coordenadas inválidas: x=${pixelCrop.x}, y=${pixelCrop.y}, width=${pixelCrop.width}, height=${pixelCrop.height}`);
      }

      // Garantir que as coordenadas não excedem as dimensões da imagem
      const cropX = Math.max(0, Math.min(pixelCrop.x, image.width));
      const cropY = Math.max(0, Math.min(pixelCrop.y, image.height));
      const cropWidth = Math.min(pixelCrop.width, image.width - cropX);
      const cropHeight = Math.min(pixelCrop.height, image.height - cropY);

      if (cropWidth <= 0 || cropHeight <= 0) {
        throw new Error(`Dimensões de recorte inválidas após ajuste: width=${cropWidth}, height=${cropHeight}`);
      }

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        throw new Error('No 2d context');
      }

      // Definir dimensões do canvas com o tamanho desejado (1200x600)
      const maxWidth = 1200;
      const maxHeight = 600;
      
      // Calcular escala para redimensionar mantendo proporção
      const scaleX = maxWidth / cropWidth;
      const scaleY = maxHeight / cropHeight;
      const scale = Math.min(scaleX, scaleY);

      // Definir tamanho do canvas (respeitando máximo de 1200x600)
      canvas.width = Math.min(Math.round(cropWidth * scale), maxWidth);
      canvas.height = Math.min(Math.round(cropHeight * scale), maxHeight);

      // Configurar qualidade de renderização
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // Desenhar a imagem recortada no canvas
      ctx.drawImage(
        image,
        cropX,
        cropY,
        cropWidth,
        cropHeight,
        0,
        0,
        canvas.width,
        canvas.height
      );

      return new Promise((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              // Se blob for null, pode ser canvas tainted
              // Tentar alternativa usando getImageData
              console.warn('toBlob retornou null, tentando alternativa...');
              try {
                const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                const newCanvas = document.createElement('canvas');
                newCanvas.width = canvas.width;
                newCanvas.height = canvas.height;
                const newCtx = newCanvas.getContext('2d');
                
                if (newCtx) {
                  newCtx.putImageData(imageData, 0, 0);
                  newCanvas.toBlob(
                    (newBlob) => {
                      if (newBlob) {
                        resolve(newBlob);
                      } else {
                        reject(new Error('Canvas tainted - não foi possível exportar a imagem. Por favor, use uma imagem local.'));
                      }
                    },
                    'image/jpeg',
                    0.9
                  );
                } else {
                  reject(new Error('Não foi possível criar contexto alternativo'));
                }
              } catch (altError) {
                reject(new Error(`Canvas tainted - não foi possível exportar a imagem. Por favor, use uma imagem local. Erro: ${altError instanceof Error ? altError.message : 'Erro desconhecido'}`));
              }
            }
          },
          'image/jpeg',
          0.9 // Qualidade JPEG
        );
      });
    } catch (error) {
      console.error('Erro detalhado no getCroppedImg:', error);
      console.error('pixelCrop recebido:', pixelCrop);
      console.error('imageSrc:', imageSrc);
      throw error;
    }
  };

  const handleSave = async () => {
    // Usar o ref se o state ainda não foi atualizado
    let areaToCrop = croppedAreaPixels || croppedAreaPixelsRef.current;
    
    if (!areaToCrop) {
      console.warn('Nenhuma área de recorte selecionada. Aguardando...');
      // Aguardar um pouco e tentar novamente
      await new Promise(resolve => setTimeout(resolve, 500));
      areaToCrop = croppedAreaPixels || croppedAreaPixelsRef.current;
      
      if (!areaToCrop) {
        alert('Por favor, aguarde a imagem carregar completamente antes de aplicar o recorte.');
        return;
      }
    }

    // Validar área de crop
    if (
      !areaToCrop ||
      areaToCrop.width <= 0 ||
      areaToCrop.height <= 0 ||
      areaToCrop.x < 0 ||
      areaToCrop.y < 0
    ) {
      alert('Área de recorte inválida. Por favor, ajuste a seleção.');
      return;
    }

    setIsProcessing(true);
    
    try {
      console.log('Iniciando recorte com área:', areaToCrop);
      const croppedBlob = await getCroppedImg(imageSrc, areaToCrop);
      console.log('Recorte concluído, tamanho do blob:', croppedBlob.size);
      
      const croppedFile = new File([croppedBlob], 'cropped-image.jpg', {
        type: 'image/jpeg',
      });
      onCropComplete(croppedFile);
    } catch (error: any) {
      console.error('Erro ao recortar imagem:', error);
      const errorMessage = error?.message || 'Erro desconhecido ao recortar a imagem';
      alert(`Erro ao recortar a imagem: ${errorMessage}. Por favor, tente novamente.`);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Recortar Imagem</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: 400,
            bgcolor: '#333',
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={aspectRatio}
            onCropChange={onCropChange}
            onZoomChange={onZoomChange}
            onCropComplete={onCropCompleteCallback}
            cropShape="rect"
            showGrid={true}
          />
        </Box>
        <Box sx={{ mt: 3, px: 2 }}>
          <Typography gutterBottom>Zoom</Typography>
          <Slider
            value={zoom}
            min={1}
            max={3}
            step={0.1}
            onChange={(e, value) => onZoomChange(value as number)}
            aria-labelledby="zoom-slider"
          />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
          Arraste a imagem para posicionar e use o zoom para ajustar
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isProcessing}>
          Cancelar
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          color="primary"
          disabled={isProcessing}
          startIcon={isProcessing ? <CircularProgress size={16} /> : null}
        >
          {isProcessing ? 'Processando...' : 'Aplicar Recorte'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageCropDialog;

